import { timeUnitOf, truncSql, timeKeySql, bucketStartSql } from '@rdrive/plugin-chart-time-series';
import { queryRows } from '@rdrive/data-duckdb';
import { makeFetchLegendTitles } from './statusHistoryLegend';

// Status-history velocity ("how much activity per period") queries for the
// activity page. SQL only: the series objects, colours, legend naming and
// tooltip markup are presentation and live in @rdrive/plugin-chart-time-series'
// BaseTimeSeriesChart (mode="periodic").
//
// Rows are keyed by status *colour*, not status id — several templates share a
// colour and the chart stacks them as one band.
//
// The date-bucket expressions come from `@rdrive/plugin-chart-time-series`' `timeBuckets` helpers.
// This config has no filter context of its own (the page owns it), so it keeps
// plain `queryRows` rather than `makeChartConfig`.
export function useStatusHistoryVelocityChart({ conn }) {
  const run = (query) => queryRows(conn, query);

  const generateActivityQuery = (groupBy) => {
    const timeUnit = timeUnitOf(groupBy);
    const dateTruncSql = truncSql(timeUnit, 'validFrom');
    // ISO-year week keys (2026-10-07): a late-December week starting ISO week 1 no longer shares
    // the previous January's key, and bucketStartSql resolves each key to its own Monday
    const bucketKeySql = timeKeySql(timeUnit, dateTruncSql, { isoYear: true });

    return `
    WITH time_periods AS (
      SELECT DISTINCT
        ${dateTruncSql} AS period_start,
        ${bucketKeySql} AS time_key
      FROM filteredStatusHistory
      WHERE validFrom IS NOT NULL
    ),
    status_activity AS (
      -- First get a count by status ID
      SELECT
        tp.time_key,
        sht.statusId,
        COUNT(DISTINCT sht.subItemId) AS activity_count
      FROM time_periods tp
      JOIN filteredStatusHistory sht
        ON ${truncSql(timeUnit, 'sht.validFrom')} = tp.period_start
      GROUP BY
        tp.time_key,
        sht.statusId
      HAVING COUNT(DISTINCT sht.subItemId) > 0
    ),
    status_with_color AS (
      -- Join with status templates to get colors
      -- Use LEFT JOIN to debug if some statuses aren't matching
      SELECT
        sa.time_key,
        sa.statusId,
        st.color,
        st.sort,
        sa.activity_count
      FROM status_activity sa
      LEFT JOIN status_templates st ON sa.statusId = st.id
    ),
    color_activity AS (
      -- Aggregate by color
      SELECT
        time_key,
        color,
        COALESCE(MIN(sort), 999) AS sort, -- Default sort value if NULL
        SUM(activity_count) AS count
      FROM status_with_color
      GROUP BY time_key, color
    ),
    all_time_periods AS (
      -- Get all time periods
      SELECT time_key
      FROM time_periods
    ),
    all_colors AS (
      -- Get all colors from templates
      SELECT DISTINCT
        color,
        COALESCE(MIN(sort), 999) AS sort
      FROM status_templates
      GROUP BY color
    ),
    all_periods_and_colors AS (
      -- Create a grid of all periods and colors
      SELECT
        tp.time_key,
        c.color,
        c.sort
      FROM all_time_periods tp
      CROSS JOIN all_colors c
    )
    -- Final result with zeros for missing combinations
    SELECT
      apc.time_key as time_key,
      apc.color as color,
      apc.sort as sort,
      COALESCE(ca.count, 0) AS count
    FROM all_periods_and_colors apc
    LEFT JOIN color_activity ca
      ON apc.time_key = ca.time_key AND apc.color = ca.color
    ORDER BY
      apc.time_key,
      apc.sort
  `;
  };

  // Query for detailed tooltip data. `bucketStartSql` THROWS on a malformed key
  // (and anchors an ISO week on Jan 4, which is the fix for this chart's old
  // Jan-1-based parsing); the base chart catches per fetch and renders an empty
  // breakdown — same user-visible result, one code path.
  const generateTooltipQuery = (timePoint, groupBy) => {
    const timeUnit = timeUnitOf(groupBy);
    const boundaryTimestampSQL = bucketStartSql(timeUnit, timePoint);

    // Query to show activity details for the selected time period, grouped by title + color
    return `
    WITH specific_time_period AS (
      -- Define the single time period we care about
      SELECT ${boundaryTimestampSQL} AS period_start
    ),
    status_counts AS (
      -- Count statuses with consistent date truncation
      SELECT
        sht.statusId,
        COUNT(DISTINCT sht.subItemId) AS activity_count
      FROM specific_time_period stp
      JOIN filteredStatusHistory sht
        ON ${truncSql(timeUnit, 'sht.validFrom')} = stp.period_start
      GROUP BY
        sht.statusId
    ),
    title_color_groups AS (
      -- Join with status templates and group by title/color
      SELECT
        st.title,
        st.color,
        MIN(st.sort) AS sort_order,
        SUM(sc.activity_count) AS activity_count
      FROM status_counts sc
      JOIN status_templates st ON sc.statusId = st.id
      GROUP BY
        st.title,
        st.color
      HAVING SUM(sc.activity_count) > 0
    )
    -- Return the grouped data
    SELECT
      title as title,
      color as color,
      sort_order AS sort,
      activity_count AS count
    FROM title_color_groups
    ORDER BY
      sort_order,
      title;
  `;
  };

  // Rows: { timeKey, color (may be null), value, sort } — `value` is the per-bucket count
  const fetchSeries = async (groupBy) => {
    const rows = await run(generateActivityQuery(groupBy));
    return rows.map((row) => ({
      timeKey: row.time_key,
      color: row.color,
      value: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Rows: { title, color, sort, count } for one time bucket
  const fetchTooltip = async (timePoint, groupBy) => {
    const rows = await run(generateTooltipQuery(timePoint, groupBy));
    return rows.map((row) => ({
      ...row,
      count: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Status titles with activity in the visible buckets, for the exported PNG's legend
  const fetchLegendTitles = makeFetchLegendTitles(conn);

  return { fetchSeries, fetchTooltip, fetchLegendTitles };
}
